import { useCallback, useMemo } from 'react'

type BaseProduct = {
  product_id: number
  product_quantity: number
}

type TUseCartActions<T extends BaseProduct> = {
  addProduct: (product: T) => T[]
  changeProductQuantity: (product_id: number, quantity: number) => T[]
  removeProduct: (product_id: number) => T[]
  changeProductFlavor: (oldProductId: number, newProductId: number) => T[]
}

const triggerStorageEvent = (key: string, value: any) => {
  localStorage.setItem(key, JSON.stringify(value))

  window.dispatchEvent(
    new StorageEvent('storage', {
      key,
      newValue: JSON.stringify(value)
    })
  )
}

export const useCartActions = <T extends BaseProduct>(currentItems: T[]): TUseCartActions<T> => {
  const memoizedItems = useMemo(() => currentItems, [currentItems])

  const addProduct = useCallback(
    (product: T): T[] => {
      const existingProduct = memoizedItems.find((item) => item.product_id === product.product_id)
      let updatedItems: T[]

      if (existingProduct) {
        updatedItems = memoizedItems.map((item) =>
          item.product_id === product.product_id
            ? { ...item, product_quantity: (item.product_quantity || 0) + 1 }
            : item
        )
      } else {
        updatedItems = [...memoizedItems, product]
      }

      triggerStorageEvent('selectedProducts', updatedItems)

      return updatedItems
    },
    [memoizedItems]
  )

  const changeProductQuantity = useCallback(
    (product_id: number, quantity: number): T[] => {
      const updatedItems = memoizedItems.map((product) =>
        product.product_id === product_id ? { ...product, product_quantity: quantity } : product
      )

      triggerStorageEvent('selectedProducts', updatedItems)

      return updatedItems
    },
    [memoizedItems]
  )

  const removeProduct = useCallback(
    (product_id: number): T[] => {
      const updatedItems = memoizedItems.filter((product) => product.product_id !== product_id)

      triggerStorageEvent('selectedProducts', updatedItems)

      return updatedItems
    },
    [memoizedItems]
  )

  const changeProductFlavor = useCallback(
    (oldProductId: number, newProductId: number): T[] => {
      const updatedItems = memoizedItems.map((product) =>
        product.product_id === oldProductId ? { ...product, product_id: newProductId } : product
      )

      triggerStorageEvent('selectedProducts', updatedItems)

      return updatedItems
    },
    [memoizedItems]
  )

  return {
    addProduct,
    changeProductQuantity,
    removeProduct,
    changeProductFlavor
  }
}
